import React, { useState, useRef, useEffect } from 'react';
import { generatePilotCostKey, calculatePilotUpgradeCost, calculatePilotUpgradeSteps, validatePilotUpgradeParams, formatNumber } from '../utils/pilotDataUtils';
import { useLanguage } from '../contexts/LanguageContext';
import { useSound } from '../contexts/SoundContext';
import StarRating from './StarRating';
import LevelRating from './LevelRating';

const rarityColorMap = {
  rare: '#FFD700',
  epic: '#800080',
  legendary: '#FFA500'
};

const getRarityColor = (rarity) => {
  if (!rarity) return '#6b7280';
  return rarityColorMap[rarity.toLowerCase()] || '#6b7280';
};

const PilotUpgradeCalculator = ({ pilots, costs }) => {
  const { t } = useLanguage();
  const { playButtonClick, playButtonHover, playSelectOptionOpen, playInterfaceSelectionHover } = useSound();
  const assetBase = typeof window !== 'undefined' ? window.location.origin : '';
  const pilotImageFallback = `${assetBase}/pilot-images/default.png`;

  const getPilotImage = (pilot) => {
    if (!pilot) {
      return pilotImageFallback;
    }
    if (pilot.image_url) {
      if (pilot.image_url.startsWith('http')) {
        return pilot.image_url;
      }
      const normalized = pilot.image_url.startsWith('/') ? pilot.image_url : `/${pilot.image_url}`;
      return `${assetBase}${normalized}`;
    }
    const slug = (pilot.name || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    return `${assetBase}/pilot-images/${slug}.png`;
  };
  
  // State for form inputs
  const [selectedPilot, setSelectedPilot] = useState(null);
  const [pilotQuery, setPilotQuery] = useState("");
  const [skillFilter, setSkillFilter] = useState("all");
  const [rarityFilter, setRarityFilter] = useState("all"); // all | rare | epic | legendary
  const [isPilotListOpen, setIsPilotListOpen] = useState(false);
  const [currentStar, setCurrentStar] = useState(1);
  const [currentLevel, setCurrentLevel] = useState(1);
  const [upgradeToMax, setUpgradeToMax] = useState(true);
  const [targetStar, setTargetStar] = useState(6);
  const [targetLevel, setTargetLevel] = useState(7);
  
  // State for results and errors
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [upgradeSteps, setUpgradeSteps] = useState([]);
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [hasSeenBreakdownHint, setHasSeenBreakdownHint] = useState(false);
  
  const selectedPilotRarityColor = getRarityColor(selectedPilot?.rarity);

  // State for My Resources
  const [useMyResources, setUseMyResources] = useState(false);
  const [myAcoins, setMyAcoins] = useState(() => {
    // Load A-coins from localStorage on initial render
    const savedAcoins = localStorage.getItem('mecharena-pilot-my-acoins');
    return savedAcoins || "";
  });
  const [myMarks, setMyMarks] = useState("");
  
  // Ref for scrolling to results
  const resultsRef = useRef(null);
  
  // Save A-coins to localStorage whenever it changes
  useEffect(() => {
    if (myAcoins !== "") {
      localStorage.setItem('mecharena-pilot-my-acoins', myAcoins);
    } else {
      // Remove from localStorage if empty
      localStorage.removeItem('mecharena-pilot-my-acoins');
    }
  }, [myAcoins]);

  // Handle pilot selection
  const handlePilotSelect = (pilotName) => {
    playInterfaceSelectionHover();
    const pilot = pilots.find(p => p.name === pilotName);
    setSelectedPilot(pilot);
    setPilotQuery(pilot ? pilot.name : "");
    setIsPilotListOpen(false);
    setError(null);
    setResults(null);
    
    // Track pilot selection in Google Analytics
    if (typeof gtag !== 'undefined') {
      gtag('event', 'pilot_selected', {
        'pilot_name': pilotName,
        'pilot_skill': pilot?.skill,
        'pilot_rarity': pilot?.rarity
      });
    }
  };

  // Filtered pilots based on skill, rarity + query
  const filteredPilots = pilots
    .filter((p) => {
      if (skillFilter === 'all') return true;
      return p.skill === skillFilter;
    })
    .filter((p) => {
      if (rarityFilter === 'all') return true;
      return p.rarity === rarityFilter;
    })
    .filter((p) => {
      const q = (pilotQuery || '').toLowerCase();
      if (!q) return true;
      return (
        p.name.toLowerCase().includes(q) ||
        (p.skill || '').toLowerCase().includes(q) ||
        (p.rarity || '').toLowerCase().includes(q)
      );
    });

  // Close dropdown on Escape
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') setIsPilotListOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // One-time hint animation timer: auto-mark seen after a short period (per session)
  useEffect(() => {
    if (results && !hasSeenBreakdownHint) {
      // Start animation after results appear
      const timer = setTimeout(() => {
        setHasSeenBreakdownHint(true);
      }, 8000); // 8s gentle pulse then stop
      return () => clearTimeout(timer);
    }
  }, [results, hasSeenBreakdownHint]);

  // Handle form submission
  const handleCalculate = () => {
    setError(null);
    setResults(null);
    setIsCalculating(true);
    setShowBreakdown(false);
    setHasSeenBreakdownHint(false); // Reset hint animation for new calculation

    if (!selectedPilot) {
      setError(t('pilotForm.selectPilotError') || 'Please select a pilot');
      setIsCalculating(false);
      return;
    }

    // Determine target values
    const finalTargetStar = upgradeToMax ? 6 : targetStar;
    const finalTargetLevel = upgradeToMax ? 7 : targetLevel;

    // Validate parameters
    const validation = validatePilotUpgradeParams(currentStar, currentLevel, finalTargetStar, finalTargetLevel);
    if (!validation.isValid) {
      setError(t(validation.error) || validation.error);
      setIsCalculating(false);
      // Track validation error
      if (typeof gtag !== 'undefined') {
        gtag('event', 'error_occurred', {
          'error_type': 'validation',
          'error_message': validation.error,
          'calculator_type': 'pilot'
        });
      }
      return;
    }

    try {
      // Generate cost key (rarity)
      const rarity = generatePilotCostKey(selectedPilot);
      
      // Calculate upgrade cost
      const totalCosts = calculatePilotUpgradeCost(
        costs,
        rarity,
        currentStar,
        currentLevel,
        finalTargetStar,
        finalTargetLevel
      );

      // Calculate upgrade steps for detailed breakdown
      const steps = calculatePilotUpgradeSteps(
        costs,
        rarity,
        currentStar,
        currentLevel,
        finalTargetStar,
        finalTargetLevel
      );

      // Calculate resource analysis if My Resources is enabled
      let resourceAnalysis = null;
      if (useMyResources && (myAcoins !== "" || myMarks !== "")) {
        const userAcoins = parseFloat(myAcoins) || 0;
        const userMarks = parseFloat(myMarks) || 0;
        const neededMarks = totalCosts.marks;
        const remainingMarks = Math.max(0, neededMarks - userMarks);
        const marksSufficient = remainingMarks === 0;
        
        // Calculate total A-coins needed (upgrade cost only, marks can't be purchased with A-coins)
        const totalAcoinsNeeded = totalCosts.acoins;
        const remainingAcoins = Math.max(0, totalAcoinsNeeded - userAcoins);
        const acoinsSufficient = remainingAcoins === 0;
        
        resourceAnalysis = {
          userAcoins,
          userMarks,
          neededMarks,
          remainingMarks,
          marksSufficient,
          totalAcoinsNeeded,
          remainingAcoins,
          acoinsSufficient,
          upgradeAcoins: totalCosts.acoins
        };
        
        // Track resource analysis result
        if (typeof gtag !== 'undefined') {
          gtag('event', 'resource_analysis', {
            'acoins_sufficient': acoinsSufficient,
            'marks_sufficient': marksSufficient,
            'total_acoins_needed': totalAcoinsNeeded,
            'calculator_type': 'pilot'
          });
        }
      }

      // Calculate XP earning details
      // Players earn 800 XP per battle win, each battle takes ~4 minutes
      const xpPerBattle = 800;
      const minutesPerBattle = 4;
      const totalBattlesNeeded = Math.ceil(totalCosts.xp / xpPerBattle);
      const totalHoursNeeded = (totalBattlesNeeded * minutesPerBattle) / 60;

      setResults({
        pilot: selectedPilot,
        from: `${currentStar}★ L${currentLevel}`,
        to: `${finalTargetStar}★ L${finalTargetLevel}`,
        costs: totalCosts,
        resourceAnalysis,
        xpEarning: {
          totalBattlesNeeded,
          totalHoursNeeded,
          xpPerBattle,
          minutesPerBattle
        }
      });
      
      setUpgradeSteps(steps);
       
       // Track calculation completion in Google Analytics
       if (typeof gtag !== 'undefined') {
         gtag('event', 'pilot_calculation_completed', {
           'pilot_name': selectedPilot.name,
           'pilot_skill': selectedPilot.skill,
           'pilot_rarity': selectedPilot.rarity,
           'from_star': currentStar,
           'from_level': currentLevel,
           'to_star': finalTargetStar,
           'to_level': finalTargetLevel,
           'total_acoins': totalCosts.acoins,
           'total_marks': totalCosts.marks,
           'total_xp': totalCosts.xp
         });
       }
       
       // Scroll to results after a short delay to ensure DOM update
       setTimeout(() => {
         resultsRef.current?.scrollIntoView({ 
           behavior: 'smooth', 
           block: 'start' 
         });
       }, 100);
    } catch (err) {
      setError(`Calculation error: ${err.message}`);
      // Track calculation error
      if (typeof gtag !== 'undefined') {
        gtag('event', 'error_occurred', {
          'error_type': 'calculation',
          'error_message': err.message,
          'calculator_type': 'pilot'
        });
      }
    } finally {
      setIsCalculating(false);
    }
  };

  // Get unique skills for filter dropdown
  const uniqueSkills = [...new Set(pilots.map(p => p.skill))].sort();

  return (
    <div className="bg-gradient-to-br from-white via-blue-50 to-indigo-50 dark:from-[#121212] dark:via-[#1E1E1E] dark:to-[#1A1A1A] rounded-2xl shadow-xl p-6 sm:p-8 lg:p-10 border border-blue-100 dark:border-[#2A2A2A] max-w-4xl mx-auto transition-colors duration-300">
      <div className="hero-animated-bg mb-6 sm:mb-8 lg:mb-10 text-center rounded-2xl p-5 sm:p-6 lg:p-7 dark:p-8 border border-blue-200/60 dark:border-[#FF8A65]/40 shadow-inner">
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent dark:bg-none">
          <span className="dark:hidden">{t('app.calculatorTitle')}</span>
          <span className="hidden dark:inline text-white [text-shadow:2px_2px_4px_#000000]">{t('app.calculatorTitle')}</span>
        </h2>
      </div>
      
      {/* Skill and Rarity Filters + Pilot Selection - Searchable Combobox */}
      <div className="mb-6 sm:mb-8">
        {/* Skill Filter */}
        <div className="mb-3">
          <label className="block text-xs sm:text-sm font-medium text-indigo-700 dark:text-white mb-1">
            {t('pilotForm.skillLabel') || 'Skill'}
          </label>
          <select
            value={skillFilter}
            onChange={(e) => { 
              setSkillFilter(e.target.value); 
              setIsPilotListOpen(true);
              // Track filter change
              if (typeof gtag !== 'undefined') {
                gtag('event', 'filter_changed', {
                  'filter_type': 'skill',
                  'filter_value': e.target.value,
                  'calculator_type': 'pilot'
                });
              }
            } }
            className="w-full px-3 py-2 rounded-lg border-2 border-indigo-200 dark:border-[#333333] bg-white dark:bg-[#1E1E1E] text-sm text-gray-800 dark:text-[#FFFFFF] focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-orange-glow focus:border-indigo-500 dark:focus:border-[#FF8A65] transition-colors"
            aria-label={t('pilotForm.skillLabel') || 'Skill'}
          >
            <option value="all">{t('pilotForm.skillAll') || 'All Skills'}</option>
            {uniqueSkills.map(skill => (
              <option key={skill} value={skill}>{skill}</option>
            ))}
          </select>
        </div>

        {/* Rarity Filter */}
        <div className="mb-3">
          <label className="block text-xs sm:text-sm font-medium text-indigo-700 dark:text-white mb-1">
            {t('form.rarityLabel') || 'Rarity'}
          </label>
          <select
            value={rarityFilter}
            onChange={(e) => { 
              setRarityFilter(e.target.value); 
              setIsPilotListOpen(true);
              // Track filter change
              if (typeof gtag !== 'undefined') {
                gtag('event', 'filter_changed', {
                  'filter_type': 'rarity',
                  'filter_value': e.target.value,
                  'calculator_type': 'pilot'
                });
              }
            } }
            className="w-full px-3 py-2 rounded-lg border-2 border-indigo-200 dark:border-[#333333] bg-white dark:bg-[#1E1E1E] text-sm text-gray-800 dark:text-[#FFFFFF] focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-orange-glow focus:border-indigo-500 dark:focus:border-[#FF8A65] transition-colors"
            aria-label={t('form.rarityLabel') || 'Rarity'}
          >
            <option value="all">{t('form.rarityAll') || 'All Rarities'}</option>
            <option value="rare">{t('form.rarityRare') || 'Rare'}</option>
            <option value="epic">{t('form.rarityEpic') || 'Epic'}</option>
            <option value="legendary">{t('form.rarityLegendary') || 'Legendary'}</option>
          </select>
        </div>

        <label className="block text-sm sm:text-base font-medium text-indigo-700 dark:text-white mb-2 sm:mb-3">
          {t('pilotForm.selectPilot') || 'Select Pilot'}
        </label>
        <div className="relative">
          <div className="flex items-center rounded-xl border-2 border-indigo-200 dark:border-[#333333] bg-white/80 dark:bg-[#1E1E1E]/80 backdrop-blur-sm focus-within:ring-2 focus-within:ring-indigo-500 focus-within:border-indigo-500 transition-all duration-200 hover:border-indigo-300 dark:hover:border-gray-500 hover:shadow-md">
            <svg className="w-5 h-5 text-indigo-400 dark:text-[#FFB38A] ml-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 104.5 4.5a7.5 7.5 0 0012.15 12.15z" />
            </svg>
            <input
              type="text"
              value={pilotQuery}
              placeholder={selectedPilot ? selectedPilot.name : (t('pilotForm.searchPlaceholder') || 'Search pilots...')}
              onFocus={() => {
                if (!isPilotListOpen) {
                  playSelectOptionOpen();
                }
                setIsPilotListOpen(true);
              }}
              onChange={(e) => { 
                if (!isPilotListOpen) {
                  playSelectOptionOpen();
                }
                setPilotQuery(e.target.value); 
                setIsPilotListOpen(true);
                // Track search query
                if (typeof gtag !== 'undefined' && e.target.value.length > 2) {
                  gtag('event', 'search_performed', {
                    'search_term_length': e.target.value.length,
                    'calculator_type': 'pilot'
                  });
                }
              } }
              className="w-full px-3 sm:px-4 py-2 sm:py-3 rounded-xl outline-none text-sm sm:text-base bg-transparent text-gray-900 dark:text-[#FFFFFF] placeholder-gray-500 dark:placeholder-gray-400"
              aria-label="Search pilots"
            />
            {selectedPilot && (
              <button
                type="button"
                className="mr-3 text-indigo-500 dark:text-[#FF7043] text-xs underline hover:text-indigo-700 dark:hover:text-[#FF8A65]"
                onClick={() => { 
                  playButtonClick();
                  setSelectedPilot(null); 
                  setPilotQuery(""); 
                  setIsPilotListOpen(true); 
                }}
              >
                {t('form.clear') || 'Clear'}
              </button>
            )}
          </div>

          {isPilotListOpen && (
            <div className="absolute z-20 mt-2 w-full max-h-64 overflow-auto bg-white dark:bg-[#1A1A1A] border-2 border-indigo-200 dark:border-[#333333] rounded-xl shadow-lg">
              {filteredPilots.length === 0 ? (
                <div className="px-4 py-3 text-sm text-gray-500 dark:text-[#8A8A8A]">{t('pilotForm.noResults') || 'No results'}</div>
              ) : (
                filteredPilots.map((pilot) => (
                  <button
                    type="button"
                    key={pilot.name}
                    onClick={() => handlePilotSelect(pilot.name)}
                    className={`w-full text-left px-4 py-2 text-sm sm:text-base hover:bg-indigo-50 dark:hover:bg-[#2F1E13] ${selectedPilot?.name === pilot.name ? 'bg-indigo-50 dark:bg-[#432615]' : ''} text-gray-900 dark:text-[#FFFFFF]`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-800 dark:text-[#FFFFFF]">{pilot.name}</span>
                      <span className="text-xs text-gray-500 dark:text-[#8A8A8A]">{pilot.skill} • {pilot.rarity}</span>
                    </div>
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        {/* Purchase Cost Display */}
        {selectedPilot && selectedPilot.cost && (
          <div className="mt-3 p-3 bg-blue-50 dark:bg-white/10 rounded-lg border border-blue-200 dark:border-[#FF8A65] dark:shadow-[0_0_20px_rgba(255,120,49,0.25)] flex items-center gap-4">
            <div className="flex-shrink-0">
              <img
                src={getPilotImage(selectedPilot)}
                alt={selectedPilot.name}
                className="w-24 h-24 sm:w-28 sm:h-28 lg:w-32 lg:h-32 rounded-2xl border-2 border-blue-200 dark:border-[#FF8A65] object-cover bg-white dark:bg-[#1A1A1A]"
                onError={(e) => {
                  if (e.target.src !== pilotImageFallback) {
                    e.target.src = pilotImageFallback;
                  }
                }}
              />
            </div>
            <div className="text-xs sm:text-sm text-blue-700 dark:text-blue-300 flex-1 space-y-1">
              <div className="font-semibold text-sm sm:text-base text-blue-900 dark:text-blue-100">
                {selectedPilot.name}
              </div>
              <div className="flex flex-wrap gap-2 text-[11px] sm:text-xs">
                <span className="inline-flex items-center gap-1 bg-white/80 dark:bg-[#1A1A1A]/80 px-2 py-1 rounded-lg border border-blue-200 dark:border-[#FF8A65] text-blue-800 dark:text-blue-200">
                  <svg className="w-3.5 h-3.5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  {t('pilotForm.skillLabel') || 'Skill'}: {selectedPilot.skill || t('pilotForm.skillAll') || 'Skill'}
                </span>
                <span
                  className="inline-flex items-center gap-1 bg-white/80 dark:bg-[#1A1A1A]/70 px-2 py-1 rounded-lg border text-slate-800 dark:text-white"
                  style={{ borderColor: selectedPilotRarityColor }}
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: selectedPilotRarityColor }}>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 1.343-3 3s1.343 3 3 3 3-1.343 3-3-1.343-3-3-3z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 5v1m-7 7h1m12 0h1m-7 7v-1m-5.657-5.657l.707-.707m8.486 0l.707.707m0-8.486l-.707.707m-8.486 0l-.707-.707" />
                  </svg>
                  {t('form.rarityLabel') || 'Rarity'}: {selectedPilot.rarity || t('form.rarityAll') || 'Rarity'}
                </span>
              </div>
              <div className="pt-1">
                <strong>{t('pilotForm.purchaseCost') || 'Purchase Cost'}:</strong>{' '}
                {selectedPilot.cost.currency === 'Paid' ? (
                  <span>{t('pilotForm.purchaseCostPaid') || 'Paid'}</span>
                ) : selectedPilot.cost.currency === 'Credits' ? (
                  <span>{formatNumber(selectedPilot.cost.amount)} {t('pilotForm.purchaseCostCredits') || 'Credits'}</span>
                ) : (
                  <span>{formatNumber(selectedPilot.cost.amount)} {t('pilotForm.purchaseCostAcoins') || 'A-Coins'}</span>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Current Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8 mb-6 sm:mb-8">
        <div className="flex justify-center sm:justify-start">
          <StarRating
            value={currentStar}
            onChange={setCurrentStar}
            maxStars={6}
            minStars={1}
            label={t('form.currentStarLevel') || 'Current Star Level'}
          />
        </div>
        
        <div className="flex justify-center sm:justify-start">
          <LevelRating
            value={currentLevel}
            onChange={setCurrentLevel}
            maxLevels={7}
            label={t('form.currentLevel') || 'Current Level'}
          />
        </div>
      </div>

      {/* Upgrade to Max Checkbox */}
      <div className="mb-6 sm:mb-8">
        <label 
          className="flex items-center p-3 sm:p-4 bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-gray-700 dark:to-gray-600 rounded-xl border-2 border-indigo-200 dark:border-[#444444] hover:border-indigo-300 dark:hover:border-gray-400 hover:from-indigo-100 hover:to-blue-100 dark:hover:from-gray-600 dark:hover:to-gray-500 transition-all duration-300 cursor-pointer hover:shadow-md hover:scale-[1.02] active:scale-[0.98]"

        >
          <input
            type="checkbox"
            checked={upgradeToMax}
            onChange={(e) => {
              playButtonClick();
              setUpgradeToMax(e.target.checked);
              // Track feature usage
              if (typeof gtag !== 'undefined') {
                gtag('event', 'feature_used', {
                  'feature_name': 'upgrade_to_max',
                  'enabled': e.target.checked,
                  'calculator_type': 'pilot'
                });
              }
            }}
            className="mr-3 w-5 h-5 text-indigo-600 border-indigo-300 rounded focus:ring-indigo-500 dark:focus:ring-orange-glow focus:ring-2"
          />
          <span className="text-base font-medium text-indigo-800 dark:text-[#FF9800]">
            {t('form.upgradeToMax') || 'Upgrade to Max'}
          </span>
        </label>
      </div>

      {/* Target Stats (only show if not upgrading to max) */}
      {!upgradeToMax && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6 sm:mb-8">
          <div className="flex justify-center sm:justify-start">
            <StarRating
              value={targetStar}
              onChange={setTargetStar}
              maxStars={6}
              minStars={1}
              label={t('form.targetStarLevel') || 'Target Star Level'}
            />
          </div>
          
          <div className="flex justify-center sm:justify-start">
            <LevelRating
              value={targetLevel}
              onChange={setTargetLevel}
              maxLevels={7}
              label={t('form.targetLevel') || 'Target Level'}
            />
          </div>
        </div>
      )}

      {/* My Resources Section */}
      <div className="mb-6 sm:mb-8">
        <label 
          className="flex items-center p-3 sm:p-4 bg-gradient-to-r from-emerald-50 to-green-50 dark:from-gray-700 dark:to-gray-600 rounded-xl border-2 border-emerald-200 dark:border-[#444444] hover:border-emerald-300 dark:hover:border-gray-400 hover:from-emerald-100 hover:to-green-100 dark:hover:from-gray-600 dark:hover:to-gray-500 transition-all duration-300 cursor-pointer hover:shadow-md hover:scale-[1.02] active:scale-[0.98]"

        >
          <input
            type="checkbox"
            checked={useMyResources}
            onChange={(e) => {
              playButtonClick();
              setUseMyResources(e.target.checked);
              if (!e.target.checked) {
                // Don't clear A-coins, just marks, so user doesn't lose their saved A-coins
                setMyMarks("");
              }
              // Track feature usage
              if (typeof gtag !== 'undefined') {
                gtag('event', 'feature_used', {
                  'feature_name': 'my_resources',
                  'enabled': e.target.checked,
                  'calculator_type': 'pilot'
                });
              }
            }}
            className="mr-3 w-5 h-5 text-emerald-600 border-emerald-300 rounded focus:ring-emerald-500 focus:ring-2"
          />
          <span className="text-base font-medium text-emerald-800 dark:text-emerald-200">
            {t('form.myResources') || 'My Resources'}
          </span>
        </label>
        {useMyResources && (
          <div className="mt-4 p-4 bg-gradient-to-r from-emerald-50 to-green-50 dark:from-gray-700 dark:to-gray-600 rounded-xl border-2 border-emerald-200 dark:border-[#444444]">
            <p className="text-sm text-emerald-700 dark:text-emerald-300 mb-4">
              {t('form.myResourcesDescription') || 'Enter your current resources to check if they are sufficient for the upgrade.'}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-emerald-800 dark:text-emerald-200 mb-2">
                  {t('form.myAcoins') || 'My A-Coins'}
                </label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={myAcoins}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "" || (parseFloat(value) >= 0 && !isNaN(value))) {
                      setMyAcoins(value);
                    }
                  }}
                  placeholder="0"
                  className="w-full px-3 py-2 rounded-lg border-2 border-emerald-200 dark:border-[#333333] bg-white dark:bg-[#1E1E1E] text-sm text-gray-800 dark:text-[#FFFFFF] focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-emerald-800 dark:text-emerald-200 mb-2">
                  {t('pilotForm.myMarks') || 'My Marks'}
                </label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={myMarks}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "" || (parseFloat(value) >= 0 && !isNaN(value))) {
                      setMyMarks(value);
                    }
                  }}
                  placeholder="0"
                  className="w-full px-3 py-2 rounded-lg border-2 border-emerald-200 dark:border-[#333333] bg-white dark:bg-[#1E1E1E] text-sm text-gray-800 dark:text-[#FFFFFF] focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Calculate Button */}
      <button
        onClick={() => {
          playButtonClick();
          handleCalculate();
        }}
        onMouseEnter={playButtonHover}
        disabled={isCalculating}
        className={`w-full py-3 sm:py-4 px-4 sm:px-6 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-orange-glow focus:ring-offset-2 text-base sm:text-lg font-semibold transition-all duration-300 transform active:scale-[0.98] shadow-lg ${
          isCalculating 
            ? 'bg-gray-400 cursor-not-allowed' 
            : 'bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 highlighter-hover btn-orange-gradient'
        }`}
      >
        <span className="flex items-center justify-center text-white">
          {isCalculating ? (
            <>
              <svg className="w-5 h-5 mr-2 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              {t('form.calculating') || 'Calculating...'}
            </>
          ) : (
            <>
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              {t('form.calculate') || 'Calculate Upgrade Cost'}
            </>
          )}
        </span>
      </button>

      {/* Error Display */}
      {error && (
        <div className="mt-4 sm:mt-6 bg-gradient-to-r from-red-50 to-pink-50 border-2 border-red-300 text-red-700 px-4 sm:px-6 py-3 sm:py-4 rounded-xl shadow-md animate-in slide-in-from-top-2 duration-300">
          <div className="flex items-center">
            <svg className="w-4 sm:w-5 h-4 sm:h-5 mr-2 animate-bounce" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <span className="text-sm sm:text-base">{error}</span>
          </div>
        </div>
      )}

      {/* Results Display */}
      {results && (
        <div 
          ref={resultsRef}
          className="mt-6 sm:mt-8 bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-2xl p-4 sm:p-6 shadow-lg animate-in slide-in-from-bottom-2 duration-500"
        >
          <h3 className="text-lg sm:text-xl font-bold text-green-800 mb-3 sm:mb-4 text-center">{t('results.totalCost') || 'Total Upgrade Cost'}</h3>
          <div className="space-y-2">
            <p className="text-sm text-green-700">
              <strong>{t('pilotForm.pilot') || 'Pilot'}:</strong> {results.pilot.name} ({results.pilot.skill} - {results.pilot.rarity})
            </p>
            <p className="text-sm text-green-700">
              <strong>{t('results.upgradeFrom', { from: results.from, to: results.to }) || `Upgrade from ${results.from} to ${results.to}`}</strong>
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mt-4 sm:mt-6">
              <div className="bg-gradient-to-br from-white to-purple-50 p-4 sm:p-6 rounded-xl border-2 border-purple-200 text-center shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105 hover:border-purple-300 hover:shadow-xl group">
                <div className="text-xs sm:text-sm font-medium text-purple-700 mb-2 sm:mb-3 group-hover:text-purple-800 transition-colors duration-200">{t('results.acoins') || 'A-Coins'}</div>
                <div className="flex items-center justify-center space-x-2">
                  <img src={`${assetBase}/acoin.webp`} alt="A-Coins" className="w-6 h-6 sm:w-8 sm:h-8 group-hover:scale-110 transition-transform duration-200" />
                  <span className="text-xl sm:text-2xl lg:text-3xl font-bold" style={{ color: "#c004ad"}}>{formatNumber(results.costs.acoins)}</span>
                </div>
              </div>
              <div className="bg-gradient-to-br from-white to-orange-50 p-4 sm:p-6 rounded-xl border-2 border-orange-200 text-center shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105 hover:border-orange-300 hover:shadow-xl group">
                <div className="text-xs sm:text-sm font-medium text-orange-700 mb-2 sm:mb-3 group-hover:text-orange-800 transition-colors duration-200">{t('pilotResults.totalMarks') || 'Marks'}</div>
                <div className="flex items-center justify-center space-x-2">
                  <img src={`${assetBase}/marks.webp`} alt="Marks" className="w-6 h-6 sm:w-8 sm:h-8 group-hover:scale-110 transition-transform duration-200" />
                  <span className="text-xl sm:text-2xl lg:text-3xl font-bold text-orange-600 group-hover:text-orange-800 transition-colors duration-200">{formatNumber(results.costs.marks)}</span>
                </div>
              </div>
              <div className="bg-gradient-to-br from-white to-blue-50 p-4 sm:p-6 rounded-xl border-2 border-blue-200 text-center shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105 hover:border-blue-300 hover:shadow-xl group">
                <div className="text-xs sm:text-sm font-medium text-blue-700 mb-2 sm:mb-3 group-hover:text-blue-800 transition-colors duration-200">{t('pilotResults.totalXp') || 'XP'}</div>
                <div className="flex items-center justify-center space-x-2">
                  <img src={`${assetBase}/xp.webp`} alt="XP" className="w-6 h-6 sm:w-8 sm:h-8 group-hover:scale-110 transition-transform duration-200" onError={(e) => { e.target.style.display = 'none'; }} />
                  <span className="text-xl sm:text-2xl lg:text-3xl font-bold text-blue-600 group-hover:text-blue-800 transition-colors duration-200">{formatNumber(results.costs.xp)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* XP Earning Details */}
          {results.xpEarning && results.costs.xp > 0 && (
            <div className="mt-6 sm:mt-8 bg-gradient-to-br from-cyan-50 to-teal-50 dark:from-gray-800 dark:to-gray-800 border-2 border-cyan-200 dark:border-cyan-500 rounded-2xl p-4 sm:p-6 shadow-lg">
              <h3 className="text-lg sm:text-xl font-bold text-cyan-800 dark:text-white mb-4 text-center">{t('pilotResults.xpEarningDetails') || 'Earn XP Through Battles'}</h3>
              <p className="text-sm text-cyan-700 dark:text-[#B0B0B0] mb-4 text-center">
                {t('pilotResults.xpEarningDescription') || 'Instead of paying A-Coins, you can earn XP by winning battles (800 XP per win, ~4 min per battle)'}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-white dark:bg-[#1E1E1E] rounded-xl p-4 border-2 border-cyan-200 dark:border-cyan-500 text-center">
                  <div className="text-xs sm:text-sm font-medium text-cyan-700 dark:text-[#FFFFFF] mb-2">{t('pilotResults.totalBattlesNeeded') || 'Total Wins Needed'}</div>
                  <div className="text-2xl sm:text-3xl font-bold text-cyan-600 dark:text-cyan-400">{formatNumber(results.xpEarning.totalBattlesNeeded)}</div>
                  <div className="text-xs text-gray-500 dark:text-[#8A8A8A] mt-1">
                    {t('pilotResults.battlesNote', { xp: formatNumber(results.costs.xp), perBattle: formatNumber(results.xpEarning.xpPerBattle) }) || 
                     `(${formatNumber(results.costs.xp)} XP ÷ ${formatNumber(results.xpEarning.xpPerBattle)} XP per win)`}
                  </div>
                </div>
                <div className="bg-white dark:bg-[#1E1E1E] rounded-xl p-4 border-2 border-cyan-200 dark:border-cyan-500 text-center">
                  <div className="text-xs sm:text-sm font-medium text-cyan-700 dark:text-[#FFFFFF] mb-2">{t('pilotResults.totalHoursNeeded') || 'Total Time Needed'}</div>
                  <div className="text-2xl sm:text-3xl font-bold text-cyan-600 dark:text-cyan-400">
                    {results.xpEarning.totalHoursNeeded < 1 
                      ? `${Math.round(results.xpEarning.totalHoursNeeded * 60)} ${t('pilotResults.minutes') || 'min'}`
                      : `${results.xpEarning.totalHoursNeeded.toFixed(1)} ${t('pilotResults.hours') || 'hrs'}`
                    }
                  </div>
                  <div className="text-xs text-gray-500 dark:text-[#8A8A8A] mt-1">
                    {t('pilotResults.timeNote', { battles: formatNumber(results.xpEarning.totalBattlesNeeded), minutes: results.xpEarning.minutesPerBattle }) || 
                     `(${formatNumber(results.xpEarning.totalBattlesNeeded)} wins × ${results.xpEarning.minutesPerBattle} min)`}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Resource Analysis Section */}
          {results.resourceAnalysis && (
            <div className="mt-6 sm:mt-8 bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 rounded-2xl p-4 sm:p-6 shadow-lg">
              <h3 className="text-lg sm:text-xl font-bold text-amber-800 mb-4 text-center">{t('results.resourceAnalysis') || 'Resource Analysis'}</h3>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                {/* Available Resources */}
                <div className="bg-white rounded-xl p-4 border-2 border-amber-200">
                  <h4 className="text-sm font-semibold text-amber-700 mb-3">{t('results.availableResources') || 'Available Resources'}</h4>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">{t('results.acoins') || 'A-Coins'}:</span>
                      <span className="text-sm font-bold" style={{ color: "#c004ad" }}>{formatNumber(results.resourceAnalysis.userAcoins)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">{t('pilotResults.totalMarks') || 'Marks'}:</span>
                      <span className="text-sm font-bold text-orange-600">{formatNumber(results.resourceAnalysis.userMarks)}</span>
                    </div>
                  </div>
                </div>
                
                {/* Needed Resources */}
                <div className="bg-white rounded-xl p-4 border-2 border-amber-200">
                  <h4 className="text-sm font-semibold text-amber-700 mb-3">{t('results.neededResources') || 'Needed Resources'}</h4>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">{t('results.acoins') || 'A-Coins'}:</span>
                      <span className="text-sm font-bold" style={{ color: "#c004ad" }}>{formatNumber(results.resourceAnalysis.totalAcoinsNeeded)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">{t('pilotResults.totalMarks') || 'Marks'}:</span>
                      <span className="text-sm font-bold text-orange-600">{formatNumber(results.resourceAnalysis.neededMarks)}</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* A-Coins Status */}
              <div className={`mt-4 p-4 rounded-xl border-2 ${
                results.resourceAnalysis.acoinsSufficient 
                  ? 'bg-green-50 border-green-200' 
                  : 'bg-red-50 border-red-200'
              }`}>
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-sm font-semibold ${
                    results.resourceAnalysis.acoinsSufficient 
                      ? 'text-green-700' 
                      : 'text-red-700'
                  }`}>
                    {results.resourceAnalysis.acoinsSufficient 
                      ? t('results.acoinsSufficient') || 'A-Coins: Sufficient'
                      : t('results.acoinsInsufficient') || 'A-Coins: Insufficient'}
                  </span>
                </div>
                
                {!results.resourceAnalysis.acoinsSufficient && (
                  <div className="mt-3 space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">{t('results.youHave') || 'You have'}:</span>
                      <span className="font-bold" style={{ color: "#c004ad" }}>{formatNumber(results.resourceAnalysis.userAcoins)}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">{t('results.totalAcoinsNeeded') || 'Total A-Coins needed'}:</span>
                      <span className="font-bold" style={{ color: "#c004ad" }}>{formatNumber(results.resourceAnalysis.totalAcoinsNeeded)}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm pt-2 border-t border-red-200">
                      <span className="text-gray-700 font-medium">{t('results.acoinsNeededExtra') || 'A-Coins needed extra'}:</span>
                      <span className="font-bold text-red-600">{formatNumber(results.resourceAnalysis.remainingAcoins)}</span>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Marks Status */}
              <div className={`mt-4 p-4 rounded-xl border-2 ${
                results.resourceAnalysis.marksSufficient 
                  ? 'bg-green-50 border-green-200' 
                  : 'bg-red-50 border-red-200'
              }`}>
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-sm font-semibold ${
                    results.resourceAnalysis.marksSufficient 
                      ? 'text-green-700' 
                      : 'text-red-700'
                  }`}>
                    {results.resourceAnalysis.marksSufficient 
                      ? t('pilotResults.marksSufficient') || 'Marks: Sufficient'
                      : t('pilotResults.marksInsufficient') || 'Marks: Insufficient'}
                  </span>
                </div>
                
                {!results.resourceAnalysis.marksSufficient && (
                  <div className="mt-3 space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">{t('results.youHave') || 'You have'}:</span>
                      <span className="font-bold text-orange-600">{formatNumber(results.resourceAnalysis.userMarks)}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">{t('results.youNeed') || 'You need'}:</span>
                      <span className="font-bold text-orange-600">{formatNumber(results.resourceAnalysis.neededMarks)}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm pt-2 border-t border-red-200">
                      <span className="text-gray-700 font-medium">{t('results.remaining') || 'Remaining'}:</span>
                      <span className="font-bold text-red-600">{formatNumber(results.resourceAnalysis.remainingMarks)}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Detailed Breakdown */}
          <div className="mt-6 sm:mt-8">
              <button
                onClick={() => {
                  const newShowBreakdown = !showBreakdown;
                  setShowBreakdown(newShowBreakdown);
                  // Track breakdown view
                  if (typeof gtag !== 'undefined') {
                    gtag('event', 'feature_used', {
                      'feature_name': 'detailed_breakdown',
                      'action': newShowBreakdown ? 'opened' : 'closed',
                      'calculator_type': 'pilot'
                    });
                  }
                }}
                className={`flex items-center space-x-2 text-sm text-green-600 hover:text-green-700 underline transition-colors duration-200 rounded-md px-2 py-1 relative ${
                !hasSeenBreakdownHint ? 'button-rotation-animation' : ''
              }`}
            >
              {!hasSeenBreakdownHint && (
                <>
                  <span className="anim-border"></span>
                  <span className="anim-border"></span>
                  <span className="anim-border"></span>
                  <span className="anim-border"></span>
                </>
              )}
              <svg className={`w-4 h-4 transition-transform duration-200 ${showBreakdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
              <span>{showBreakdown ? t('results.hideBreakdown') || 'Hide Detailed Breakdown' : t('results.showBreakdown') || 'Show Detailed Breakdown'}</span>
            </button>
            
            {showBreakdown && (
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-sm border border-green-200 rounded-lg bg-white shadow-sm">
                  <thead>
                    <tr className="bg-gradient-to-r from-green-100 to-emerald-100">
                      <th className="px-3 py-3 text-left border-b border-green-200 font-semibold text-green-800">{t('results.step') || 'Step'}</th>
                      <th className="px-3 py-3 text-right border-b border-green-200 font-semibold" style={{color: "#c004ad"}}>{t('results.acoins') || 'A-Coins'}</th>
                      <th className="px-3 py-3 text-right border-b border-green-200 font-semibold" style={{color: "#a0039a"}}>{t('results.totalAcoins') || 'Total A-Coins'}</th>
                      <th className="px-3 py-3 text-right border-b border-green-200 font-semibold text-orange-700">{t('pilotResults.totalMarks') || 'Marks'}</th>
                      <th className="px-3 py-3 text-right border-b border-green-200 font-semibold text-orange-800">{t('pilotResults.totalMarks') || 'Total Marks'}</th>
                      <th className="px-3 py-3 text-right border-b border-green-200 font-semibold text-blue-700">{t('pilotResults.totalXp') || 'XP'}</th>
                      <th className="px-3 py-3 text-right border-b border-green-200 font-semibold text-blue-800">{t('pilotResults.totalXp') || 'Total XP'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {upgradeSteps.map((step, index) => (
                      <tr key={index} className="border-b border-green-200 hover:bg-green-50 transition-colors duration-200">
                        <td className="px-3 py-3 font-medium text-gray-800">{step.from} → {step.to}</td>
                        <td className="px-3 py-3 text-right font-medium" style={{color: "#c004ad"}}>{formatNumber(step.acoins)}</td>
                        <td className="px-3 py-3 text-right font-semibold" style={{color: "#a0039a"}}>{formatNumber(step.totalAcoins)}</td>
                        <td className="px-3 py-3 text-right text-orange-600 font-medium">{formatNumber(step.marks)}</td>
                        <td className="px-3 py-3 text-right font-semibold text-orange-700">{formatNumber(step.totalMarks)}</td>
                        <td className="px-3 py-3 text-right text-blue-600 font-medium">{formatNumber(step.xp)}</td>
                        <td className="px-3 py-3 text-right font-semibold text-blue-700">{formatNumber(step.totalXp)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                
                {/* Summary Row */}
                {upgradeSteps.length > 0 && (
                  <div className="mt-4 p-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg">
                    <div className="text-center">
                      <h4 className="text-sm font-semibold text-green-800 mb-2">{t('results.totalCost') || 'Total Upgrade Cost'}</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div className="text-center">
                          <div className="text-xs font-medium" style={{color: "#c004ad"}}>A-Coins</div>
                          <div className="text-lg font-bold" style={{color: "#a0039a"}}>{formatNumber(upgradeSteps[upgradeSteps.length - 1]?.totalAcoins || 0)}</div>
                        </div>
                        <div className="text-center">
                          <div className="text-xs text-orange-600 font-medium">{t('pilotResults.totalMarks') || 'Marks'}</div>
                          <div className="text-lg font-bold text-orange-700">{formatNumber(upgradeSteps[upgradeSteps.length - 1]?.totalMarks || 0)}</div>
                        </div>
                        <div className="text-center">
                          <div className="text-xs text-blue-600 font-medium">{t('pilotResults.totalXp') || 'XP'}</div>
                          <div className="text-lg font-bold text-blue-700">{formatNumber(upgradeSteps[upgradeSteps.length - 1]?.totalXp || 0)}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PilotUpgradeCalculator;

